import type { QuickFillItem, QuickFillRowMode } from '../../../shared/types';
import type { QuickFillSnapshotShapeOption } from '../../../shared/api/grpc/familyClient';
import { perf } from '../../../shared/lib/perfLogger';
import { normalizeRevitParameterName } from '../../../shared/lib/revitParameterNames';
import { registerRevitModelCacheInvalidator } from '../../../shared/lib/revit/modelCacheInvalidation';
import {
  EMPTY_RESOLVED,
  isMeaningful,
  normalize,
  rowKey,
  stableTypeKey,
  type AvailableParameterMeta,
  type AvailableParameterScopeEntry,
  type CheckedProperty,
  type CheckedPropertyWithLookup,
  type ParameterBindingScope,
  type PipeElement,
  type PipeFamily,
  type ProjectVisibleType,
  type ProjectVisibleTypeIndex,
  type QuickFillBuiltDatasetCacheEntry,
  type QuickFillDatasetCacheKey,
  type RenderableFamilyType,
  type ResolvedParam,
} from './quickFillDataTypes';
import {
  applyModelElementIdsToItem,
  buildFamilyTypeKey,
  buildInstanceRowLabel,
  buildRenderableFamilyTypeKey,
  createInstanceQuickFillItem,
  getItemFamilyTypeId,
  getRenderableFamilyName,
  getRenderableTypeId,
  getRenderableTypesForFamily,
  resolveModelElementIdsForFamilyType,
} from './quickFillItemBuilders';
import {
  buildInstanceParamIndex,
  buildInstanceTargetIndex,
  buildModelElementIdIndex,
  buildProjectVisibleTypeIndex,
  buildProjectElementIndexes,
  getProjectElementIndexes,
  resetQuickFillProjectElementIndexCache,
  type ProjectElementIndexes,
} from './quickFillProjectIndexes';
import {
  applyResolvedMetadataToItem,
  applyResolvedValueToItem,
  applySourceResolvedValueToItem,
  buildAvailableParameterScopeIndex,
  buildPopulationSummary,
  buildSampleRows,
  emitPopulationDiagnostics,
  isEditedQuickFillCell,
  resolveParamValue,
} from './quickFillParameterResolution';

const getPropertyBindingScopeHint = (
  property: { isInstance?: boolean },
  availableScopeIndex: Map<string, AvailableParameterScopeEntry>,
  normalizedParameterName: string
): ParameterBindingScope | undefined => {
  if (property.isInstance === true) {
    return 'instance';
  }
  if (property.isInstance === false) {
    return 'type';
  }

  const availableScope = availableScopeIndex.get(normalizedParameterName);
  if (!availableScope) {
    return undefined;
  }

  if (availableScope.instance && !availableScope.type) {
    return 'instance';
  }
  if (availableScope.type && !availableScope.instance) {
    return 'type';
  }

  return undefined;
};


const parseItemName = (itemName: string): { familyName: string; typeName: string | null } => {
  const separatorIndex = itemName.indexOf(':');
  if (separatorIndex < 0) return { familyName: itemName, typeName: null };
  return { familyName: itemName.slice(0, separatorIndex), typeName: itemName.slice(separatorIndex + 1) };
};

const buildTypeParamMap = (familyType: PipeFamily['types'][number] | null): Map<string, ResolvedParam> => {
  const map = new Map<string, ResolvedParam>();
  if (!familyType) return map;
  const paramValues = Array.isArray(familyType.parameterValues) ? familyType.parameterValues : [];
  for (const pv of paramValues) {
    if (!pv || !pv.parameterName) continue;
    map.set(pv.parameterName, {
      value: isMeaningful(pv.value) ? String(pv.value) : '',
      isReadOnly: pv.isReadOnly === true,
      hasParameter: true,
      bindingScope: pv.isInstance === true ? 'instance' : 'type',
    });
  }
  return map;
};

const EMPTY_TYPE_PARAM_MAP = new Map<string, ResolvedParam>();










// ---------------------------------------------------------------------------
// Yield to event loop — lets the browser repaint & handle user input
// ---------------------------------------------------------------------------
const yieldToMain = (): Promise<void> => new Promise(resolve => setTimeout(resolve, 0));

// How many families to process per chunk before yielding
const FAMILIES_PER_CHUNK = 15;
const MAX_PROGRESS_UPDATES_PER_BUILD = 20;
const MAX_BUILT_DATASET_CACHE_ENTRIES = 8;
const quickFillBuiltDatasetCache = new Map<string, QuickFillBuiltDatasetCacheEntry>();
let quickFillBuiltDatasetCacheGeneration = 0;

registerRevitModelCacheInvalidator((event) => {
  if (quickFillBuiltDatasetCache.size > 0) {
    perf.log('QF-Builder', 'Clearing built dataset cache', {
      reason: event.reason,
      cacheSize: quickFillBuiltDatasetCache.size,
    });
  }

  quickFillBuiltDatasetCacheGeneration += 1;
  quickFillBuiltDatasetCache.clear();
  resetQuickFillProjectElementIndexCache();
});

const resolveProgressChunkSize = (total: number): number =>
  Math.max(FAMILIES_PER_CHUNK, Math.ceil(Math.max(1, total) / MAX_PROGRESS_UPDATES_PER_BUILD));

const shouldReportChunkProgress = (processed: number, total: number): boolean => {
  if (total <= 0 || processed <= 0) {
    return false;
  }

  const chunkSize = resolveProgressChunkSize(total);
  if (total <= chunkSize) {
    return false;
  }

  return processed >= total || processed % chunkSize === 0;
};

const buildCurrentRevitParameters = (properties: any[]): Record<string, string> => {
  const currentRevitParameters: Record<string, string> = {};
  properties.forEach((prop: any) => {
    if (prop?.id && prop?.revitParameter) currentRevitParameters[prop.id] = prop.revitParameter;
  });
  return currentRevitParameters;
};

const cloneQuickFillDataRows = (rows: QuickFillItem[] | null): QuickFillItem[] | null =>
  rows
    ? (typeof structuredClone === 'function'
        ? structuredClone(rows)
        : JSON.parse(JSON.stringify(rows))) as QuickFillItem[]
    : null;

const buildDatasetCacheKey = (
  rowMode: QuickFillRowMode,
  snapshotShape: QuickFillSnapshotShapeOption
): QuickFillDatasetCacheKey => `${rowMode}:${snapshotShape}`;

const buildCheckedPropertiesSignature = (
  properties: any[],
  extraProperties: any[]
): string => {
  const safeProperties = Array.isArray(properties) ? properties : [];
  const safeExtras = Array.isArray(extraProperties) ? extraProperties : [];
  return [...safeProperties, ...safeExtras]
    .filter((property) => property?.checked)
    .map((property) => [
      String(property?.id || '').trim(),
      String(property?.label || '').trim(),
      normalizeRevitParameterName(String(property?.revitParameter || '')).trim(),
      property?.isInstance === true ? 'instance' : property?.isInstance === false ? 'type' : 'auto',
    ].join('~'))
    .join('|');
};

const buildBuiltDatasetCacheKey = (
  datasetSourceKey: string | null | undefined,
  rowMode: QuickFillRowMode,
  snapshotShape: QuickFillSnapshotShapeOption,
  properties: any[],
  extraProperties: any[]
): string | null => {
  const sourceKey = String(datasetSourceKey || '').trim();
  if (!sourceKey) {
    return null;
  }

  return [
    sourceKey,
    `row:${rowMode}`,
    `shape:${snapshotShape}`,
    `columns:${buildCheckedPropertiesSignature(properties, extraProperties)}`,
  ].join('::');
};

const cloneBuiltDatasetCacheEntry = (
  entry: QuickFillBuiltDatasetCacheEntry | undefined
): QuickFillBuiltDatasetCacheEntry | null => {
  if (!entry) {
    return null;
  }

  return {
    rows: cloneQuickFillDataRows(entry.rows) || [],
    currentRevitParameters: { ...entry.currentRevitParameters },
  };
};

const rememberBuiltDatasetCacheEntry = (
  cacheKey: string | null,
  rows: QuickFillItem[],
  currentRevitParameters: Record<string, string>
) => {
  if (!cacheKey || !Array.isArray(rows)) {
    return;
  }

  if (quickFillBuiltDatasetCache.has(cacheKey)) {
    quickFillBuiltDatasetCache.delete(cacheKey);
  }

  quickFillBuiltDatasetCache.set(cacheKey, {
    rows: cloneQuickFillDataRows(rows) || [],
    currentRevitParameters: { ...currentRevitParameters },
  });

  while (quickFillBuiltDatasetCache.size > MAX_BUILT_DATASET_CACHE_ENTRIES) {
    const oldestKey = quickFillBuiltDatasetCache.keys().next().value;
    if (!oldestKey) {
      break;
    }
    quickFillBuiltDatasetCache.delete(oldestKey);
  }
};

const rememberBuiltDatasetCacheEntryWithPerf = (
  cacheKey: string | null,
  rows: QuickFillItem[],
  currentRevitParameters: Record<string, string>,
  details: Record<string, unknown>,
  label = 'remember built dataset cache entry'
) => {
  const cacheGeneration = quickFillBuiltDatasetCacheGeneration;
  const remember = () => {
    if (cacheGeneration !== quickFillBuiltDatasetCacheGeneration) {
      return;
    }

    const tRememberCache = performance.now();
    rememberBuiltDatasetCacheEntry(cacheKey, rows, currentRevitParameters);
    perf.slow('QF-Builder', label, tRememberCache, rows.length > 1000 ? 180 : 30, {
      ...details,
      rows: rows.length,
      cacheSize: quickFillBuiltDatasetCache.size,
    });
  };

  if (rows.length > 1000 && typeof window !== 'undefined') {
    const idleWindow = window as Window & {
      requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
    };
    if (idleWindow.requestIdleCallback) {
      idleWindow.requestIdleCallback(remember, { timeout: 2000 });
      return;
    }

    window.setTimeout(remember, 250);
    return;
  }

  remember();
};

export const __resetQuickFillDataBuilderCacheForTests = () => {
  quickFillBuiltDatasetCacheGeneration += 1;
  quickFillBuiltDatasetCache.clear();
  resetQuickFillProjectElementIndexCache();
};

// ---------------------------------------------------------------------------
// Async chunked builder — processes families in batches, yielding between them
// ---------------------------------------------------------------------------
export async function buildQuickFillDataAsync(
  pipeFamilies: PipeFamily[],
  pipeElements: PipeElement[],
  availableParameters: AvailableParameterMeta[],
  properties: any[],
  extraProperties: any[],
  existingQuickFillData: QuickFillItem[],
  editedCells: Record<string, boolean>,
  dataAlreadyInitialized: boolean,
  rowMode: QuickFillRowMode,
  prevRevitParameters: Record<string, string>,
  isStillValid: () => boolean,
  onProgress: (built: number, total: number) => void,
  snapshotShape: QuickFillSnapshotShapeOption = 'familyType',
): Promise<{
  data: QuickFillItem[] | null;
  currentRevitParameters: Record<string, string>;
}> {
  const tBuild = performance.now();

  const safeExtras = Array.isArray(extraProperties) ? extraProperties : [];
  const allProperties = [...properties, ...safeExtras];
  const availableParameterNames = new Set(
    (Array.isArray(availableParameters) ? availableParameters : [])
      .map((parameter) => normalize(normalizeRevitParameterName(parameter?.name || '')))
      .filter(Boolean)
  );
  const availableParameterScopeIndex = buildAvailableParameterScopeIndex(availableParameters);
  const checkedProps: CheckedPropertyWithLookup[] = allProperties
    .filter(p => p && p.checked)
    .map((property) => {
      const normalizedRevitParameterName = normalize(
        normalizeRevitParameterName(property.revitParameter || '')
      );
      return {
        ...property,
        normalizedRevitParameterName,
        bindingScopeHint: getPropertyBindingScopeHint(
          property,
          availableParameterScopeIndex,
          normalizedRevitParameterName
        ),
      };
    });
  if (checkedProps.length === 0) {
    perf.warn('QF-Builder', 'Skipped build because there are no checked Quick Fill properties', {
      properties: Array.isArray(properties) ? properties.length : 0,
      extraProperties: safeExtras.length,
    });
    return { data: null, currentRevitParameters: prevRevitParameters };
  }

  const checkedPropsByLabel = new Map(checkedProps.map(p => [p.label, p]));
  const familiesByName = new Map(pipeFamilies.map(f => [normalize(f.name), f]));
  const familiesByTypeId = new Map<string, PipeFamily>();
  const familyTypeLookupCache = new WeakMap<PipeFamily, {
    byTypeId: Map<string, PipeFamily['types'][number]>;
    byTypeName: Map<string, PipeFamily['types'][number]>;
  }>();
  const typeParamMapCache = new WeakMap<PipeFamily['types'][number], Map<string, ResolvedParam>>();
  pipeFamilies.forEach((family) => {
    (Array.isArray(family.types) ? family.types : []).forEach((familyType) => {
      const typeKey = stableTypeKey(familyType.id);
      if (typeKey && !familiesByTypeId.has(typeKey)) {
        familiesByTypeId.set(typeKey, family);
      }
    });
  });

  const findFamilyByNameOrTypeId = (familyName: string, typeId?: string | null): PipeFamily | undefined => {
    const byName = familiesByName.get(normalize(familyName));
    if (byName) {
      return byName;
    }

    const typeKey = stableTypeKey(typeId);
    return typeKey ? familiesByTypeId.get(typeKey) : undefined;
  };

  const getFamilyTypeLookup = (family: PipeFamily) => {
    let lookup = familyTypeLookupCache.get(family);
    if (lookup) {
      return lookup;
    }

    lookup = {
      byTypeId: new Map<string, PipeFamily['types'][number]>(),
      byTypeName: new Map<string, PipeFamily['types'][number]>(),
    };
    const types = Array.isArray(family.types) ? family.types : [];
    for (const familyType of types) {
      const typeIdKey = stableTypeKey(familyType.id);
      if (typeIdKey) {
        lookup.byTypeId.set(typeIdKey, familyType);
      }

      lookup.byTypeName.set(normalize(familyType.name), familyType);
    }
    familyTypeLookupCache.set(family, lookup);
    return lookup;
  };

  const getTypeParamMap = (familyType: PipeFamily['types'][number] | null): Map<string, ResolvedParam> => {
    if (!familyType) {
      return EMPTY_TYPE_PARAM_MAP;
    }

    let cached = typeParamMapCache.get(familyType);
    if (!cached) {
      cached = buildTypeParamMap(familyType);
      typeParamMapCache.set(familyType, cached);
    }

    return cached;
  };

  const currentRevitParameters: Record<string, string> = {};
  properties.forEach((prop: any) => {
    if (prop?.id && prop?.revitParameter) currentRevitParameters[prop.id] = prop.revitParameter;
  });
  const revitParametersChanged = Object.keys(currentRevitParameters).some(
    id => prevRevitParameters[id] !== currentRevitParameters[id]
  );

  let existingData = cloneQuickFillDataRows(Array.isArray(existingQuickFillData) ? existingQuickFillData : []) ?? [];
  perf.log('QF-Builder', 'Build inputs prepared', {
    families: pipeFamilies.length,
    elements: pipeElements.length,
    availableParameters: availableParameterNames.size,
    rowMode,
    snapshotShape,
    checkedProperties: checkedProps.map((prop) => `${prop.label}:${prop.revitParameter || '<none>'}`),
    existingRows: existingData.length,
    dataAlreadyInitialized,
    revitParametersChanged,
    editedCells: Object.keys(editedCells || {}).length,
  });

  const findMatchingType = (family: PipeFamily, typeName: string | null, typeId?: string | null) => {
    const typeLookup = getFamilyTypeLookup(family);
    const normalizedTypeId = stableTypeKey(typeId);
    if (normalizedTypeId) {
      const matchById = typeLookup.byTypeId.get(normalizedTypeId);
      if (matchById) {
        return matchById;
      }
    }

    if (!typeName) return null;
    return typeLookup.byTypeName.get(normalize(typeName)) ?? null;
  };

  if (rowMode === 'instance') {
    const newData: QuickFillItem[] = [];
    const totalElements = pipeElements.length;

    for (let elementIndex = 0; elementIndex < totalElements; elementIndex++) {
      const element = pipeElements[elementIndex];
      if (!element?.familyName) {
        continue;
      }

      const matchingFamily = findFamilyByNameOrTypeId(element.familyName, element.typeId);
      const matchingType = matchingFamily
        ? findMatchingType(matchingFamily, element.typeName ?? null, element.typeId)
        : null;
      const typeMap = getTypeParamMap(matchingType);
      newData.push(
        createInstanceQuickFillItem(
          element,
          checkedProps,
          typeMap,
          availableParameterNames,
          snapshotShape
        )
      );

      const processedElements = elementIndex + 1;
      if (shouldReportChunkProgress(processedElements, totalElements)) {
        onProgress(processedElements, totalElements);
        await yieldToMain();
        if (!isStillValid()) return { data: null, currentRevitParameters };
      }
    }

    emitPopulationDiagnostics('Instance build', newData, checkedProps, {
      elementsProcessed: totalElements,
      rowMode,
      groupedIndex: 'skipped',
    });
    perf.measure('QF-Builder', 'Instance build done', tBuild, {
      rows: newData.length,
      cols: checkedProps.length,
    });
    return { data: newData, currentRevitParameters };
  }

  perf.log('QF-Builder', 'Building family-type project instance index...');
  const projectElements = Array.isArray(pipeElements) ? pipeElements : [];
  const hasProjectElementRows = projectElements.length > 0;
  const tProjectIndex = performance.now();
  const {
    instanceParamIndex,
    instanceTargetIndex,
    modelElementIdIndex,
    projectVisibleTypeIndex,
    projectVisibleTypeCount,
    projectVisibleTypeIdSamples,
    projectVisibleRowSamples,
    elementsWithPayload,
    cacheHit: projectIndexCacheHit,
  } = getProjectElementIndexes(projectElements);
  perf.slow('QF-Builder', 'family-type project index lookup', tProjectIndex, projectIndexCacheHit ? 25 : 140, {
    cacheHit: projectIndexCacheHit,
    elements: projectElements.length,
    indexedRowKeys: instanceParamIndex.size,
    indexedTargetRowKeys: instanceTargetIndex.size,
  });
  perf.log('QF-Builder', 'Family-type project instance index ready', {
    indexedRowKeys: instanceParamIndex.size,
    indexedTargetRowKeys: instanceTargetIndex.size,
    indexedFamilyTypeIds: projectVisibleTypeIndex.byTypeId.size,
    projectVisibleFamilies: projectVisibleTypeIndex.byFamilyName.size,
    projectVisibleTypes: projectVisibleTypeCount,
    projectVisibleTypeIdSamples,
    projectVisibleRowSamples,
    elementsWithPayload,
  });

  const currentSnapshotRowNames = new Set<string>();
  const currentSnapshotRowTypeIds = new Set<string>();
  for (const family of pipeFamilies) {
    const renderableTypes = getRenderableTypesForFamily(family, projectVisibleTypeIndex, hasProjectElementRows);
    if (renderableTypes.length === 0 && !hasProjectElementRows) {
      currentSnapshotRowNames.add(buildFamilyTypeKey(family.name, null));
      continue;
    }

    for (const renderableType of renderableTypes) {
      currentSnapshotRowNames.add(buildRenderableFamilyTypeKey(family.name, renderableType));
      const typeKey = stableTypeKey(getRenderableTypeId(renderableType));
      if (typeKey) {
        currentSnapshotRowTypeIds.add(typeKey);
      }
    }
  }

  if (currentSnapshotRowNames.size > 0 || currentSnapshotRowTypeIds.size > 0) {
    const existingRowsBeforePrune = existingData.length;
    const prunedStaleSnapshotSamples = existingData
      .filter((item) => {
        if (currentSnapshotRowNames.has(item.Name)) {
          return false;
        }

        const typeKey = stableTypeKey(getItemFamilyTypeId(item));
        return !(typeKey && currentSnapshotRowTypeIds.has(typeKey));
      })
      .slice(0, 8)
      .map((item) => item.Name);
    existingData = existingData.filter((item) => {
      if (currentSnapshotRowNames.has(item.Name)) {
        return true;
      }

      const typeKey = stableTypeKey(getItemFamilyTypeId(item));
      return Boolean(typeKey && currentSnapshotRowTypeIds.has(typeKey));
    });
    const prunedStaleSnapshotRows = existingRowsBeforePrune - existingData.length;
    if (prunedStaleSnapshotRows > 0) {
      perf.log('QF-Builder', 'Pruned stale family type rows absent from current snapshot', {
        before: existingRowsBeforePrune,
        after: existingData.length,
        prunedStaleSnapshotRows,
        prunedStaleSnapshotSamples,
      });
    }
  }

  // Yield after index build
  await yieldToMain();
  if (!isStillValid()) return { data: null, currentRevitParameters };

  const findProjectRenderableType = (
    family: PipeFamily,
    typeName: string | null,
    typeId?: string | null
  ): RenderableFamilyType | null => {
    const renderableTypes = getRenderableTypesForFamily(family, projectVisibleTypeIndex, hasProjectElementRows);
    const normalizedTypeId = stableTypeKey(typeId);
    if (normalizedTypeId) {
      const matchById = renderableTypes.find(
        (renderableType) => stableTypeKey(getRenderableTypeId(renderableType)) === normalizedTypeId
      );
      if (matchById) {
        return matchById;
      }
    }

    if (!typeName) {
      return null;
    }

    const normalizedTypeName = normalize(typeName);
    return renderableTypes.find((renderableType) => normalize(renderableType.name) === normalizedTypeName) ?? null;
  };

  // ── INCREMENTAL UPDATE PATH ──
  if (rowMode === 'familyType' && dataAlreadyInitialized && existingData.length > 0) {
    let dataModified = currentSnapshotRowNames.size > 0 && existingQuickFillData.length !== existingData.length;
    const existingColumns = Object.keys(existingData[0]);
    const columnsToAdd = checkedProps.map(p => p.label).filter(l => !existingColumns.includes(l));
    const existingNames = new Set(existingData.map(item => item.Name));

    // Add new columns
    if (columnsToAdd.length > 0) {
      for (let i = 0; i < existingData.length; i++) {
        const item = existingData[i];
        const { familyName, typeName } = parseItemName(item.Name);
        const itemFamilyTypeId = getItemFamilyTypeId(item);
        const matchingFamily = findFamilyByNameOrTypeId(familyName, itemFamilyTypeId);
        const matchingRenderableType = matchingFamily ? findProjectRenderableType(matchingFamily, typeName, itemFamilyTypeId) : null;
        const matchingType = matchingRenderableType?.familyType ?? (matchingFamily ? findMatchingType(matchingFamily, typeName, itemFamilyTypeId) : null);
        const matchingTypeId = matchingRenderableType ? getRenderableTypeId(matchingRenderableType) : matchingType?.id;
        const rowFamilyName = matchingRenderableType
          ? getRenderableFamilyName(familyName, matchingRenderableType)
          : familyName;
        const rowTypeName = matchingRenderableType?.name ?? typeName;
        const typeMap = getTypeParamMap(matchingType);
        if (matchingTypeId) {
          (item as any).__familyTypeId = matchingTypeId;
        }
        applyModelElementIdsToItem(item, rowFamilyName, rowTypeName, matchingTypeId, modelElementIdIndex);
        for (const col of columnsToAdd) {
          if (!(col in item)) {
            const prop = checkedPropsByLabel.get(col);
            const resolved = prop?.revitParameter
              ? resolveParamValue(
                  rowFamilyName,
                  rowTypeName,
                  typeMap,
                  prop.revitParameter,
                  instanceParamIndex,
                  availableParameterNames,
                  prop.bindingScopeHint,
                  instanceTargetIndex,
                  matchingTypeId
                )
              : EMPTY_RESOLVED;
            applyResolvedValueToItem(item, col, resolved, true);
          }
        }
        if (i % FAMILIES_PER_CHUNK === 0 && i > 0) {
          onProgress(i, existingData.length);
          await yieldToMain();
          if (!isStillValid()) return { data: null, currentRevitParameters };
        }
      }
      dataModified = true;
    }

    let refreshedCells = 0;
    let refreshedMetadata = 0;
    let skippedEditedCells = 0;

    for (let i = 0; i < existingData.length; i++) {
      const item = existingData[i];
      const { familyName, typeName } = parseItemName(item.Name);
      const itemFamilyTypeId = getItemFamilyTypeId(item);
      const matchingFamily = findFamilyByNameOrTypeId(familyName, itemFamilyTypeId);
      if (!matchingFamily) {
        continue;
      }

      const matchingRenderableType = findProjectRenderableType(matchingFamily, typeName, itemFamilyTypeId);
      const matchingType = matchingRenderableType?.familyType ?? findMatchingType(matchingFamily, typeName, itemFamilyTypeId);
      const matchingTypeId = matchingRenderableType ? getRenderableTypeId(matchingRenderableType) : matchingType?.id;
      const rowFamilyName = matchingRenderableType
        ? getRenderableFamilyName(familyName, matchingRenderableType)
        : familyName;
      const rowTypeName = matchingRenderableType?.name ?? typeName;
      const rowName = buildFamilyTypeKey(rowFamilyName, rowTypeName);
      const typeMap = getTypeParamMap(matchingType);
      if (item.Name !== rowName) {
        existingNames.delete(item.Name);
        item.Name = rowName;
        existingNames.add(rowName);
        dataModified = true;
      }
      if ((item as any).__familyTypeKey !== rowName) {
        (item as any).__familyTypeKey = rowName;
        dataModified = true;
      }
      if (matchingTypeId) {
        (item as any).__familyTypeId = matchingTypeId;
      }
      if (applyModelElementIdsToItem(item, rowFamilyName, rowTypeName, matchingTypeId, modelElementIdIndex)) {
        dataModified = true;
      }
      for (const prop of checkedProps) {
        if (!prop.revitParameter) {
          continue;
        }

        const resolved = resolveParamValue(
          rowFamilyName,
          rowTypeName,
          typeMap,
          prop.revitParameter,
          instanceParamIndex,
          availableParameterNames,
          prop.bindingScopeHint,
          instanceTargetIndex,
          matchingTypeId
        );
        const preserveValue = isEditedQuickFillCell(editedCells, i, prop.label);
        const changed = applySourceResolvedValueToItem(item, prop.label, resolved, preserveValue);
        if (changed) {
          dataModified = true;
          if (preserveValue) {
            refreshedMetadata += 1;
          } else {
            refreshedCells += 1;
          }
        }
        if (preserveValue) {
          skippedEditedCells += 1;
        }
      }
    }

    // Add new families
    for (const family of pipeFamilies) {
      const renderableTypes = getRenderableTypesForFamily(family, projectVisibleTypeIndex, hasProjectElementRows);
      if (renderableTypes.length === 0) {
        if (hasProjectElementRows) {
          continue;
        }

        if (!existingNames.has(family.name)) {
          const item: QuickFillItem = { Name: family.name };
          checkedProps.forEach((p) => {
            const resolved = p.revitParameter
              ? resolveParamValue(
                  family.name,
                  null,
                  new Map(),
                  p.revitParameter,
                  instanceParamIndex,
                  availableParameterNames,
                  p.bindingScopeHint,
                  instanceTargetIndex
                )
              : EMPTY_RESOLVED;
            applyResolvedValueToItem(item, p.label, resolved, true);
          });
          (item as any).__rowMode = 'familyType';
          (item as any).__familyTypeKey = buildFamilyTypeKey(family.name, null);
          (item as any).__familyTypeId = '';
          (item as any).__category = family.category || '';
          applyModelElementIdsToItem(item, family.name, null, null, modelElementIdIndex);
          existingData.push(item);
          existingNames.add(family.name);
          dataModified = true;
        }
      } else {
        for (const ft of renderableTypes) {
          const rowFamilyName = getRenderableFamilyName(family.name, ft);
          const name = buildFamilyTypeKey(rowFamilyName, ft.name);
          if (!existingNames.has(name)) {
            const item: QuickFillItem = { Name: name };
            const typeMap = getTypeParamMap(ft.familyType);
            checkedProps.forEach(p => {
              const resolved = p.revitParameter
                ? resolveParamValue(
                    rowFamilyName,
                    ft.name,
                    typeMap,
                    p.revitParameter,
                    instanceParamIndex,
                    availableParameterNames,
                    p.bindingScopeHint,
                    instanceTargetIndex,
                    getRenderableTypeId(ft)
                  )
                : EMPTY_RESOLVED;
              applyResolvedValueToItem(item, p.label, resolved, true);
            });
            (item as any).__rowMode = 'familyType';
            (item as any).__familyTypeKey = name;
            (item as any).__familyTypeId = getRenderableTypeId(ft) ?? '';
            (item as any).__category = family.category || '';
            applyModelElementIdsToItem(item, rowFamilyName, ft.name, getRenderableTypeId(ft), modelElementIdIndex);
            existingData.push(item);
            existingNames.add(name);
            dataModified = true;
          }
        }
      }
    }

    if (dataModified) {
      emitPopulationDiagnostics('Incremental update', existingData, checkedProps, {
        addedColumns: columnsToAdd.length,
        revitParametersChanged,
        refreshedCells,
        refreshedMetadata,
        skippedEditedCells,
      });
      perf.measure('QF-Builder', 'Incremental update done', tBuild, { rows: existingData.length });
      return { data: [...existingData], currentRevitParameters };
    }
    emitPopulationDiagnostics('Incremental update (no-op)', existingData, checkedProps, {
      addedColumns: columnsToAdd.length,
      revitParametersChanged,
      refreshedCells,
      refreshedMetadata,
      skippedEditedCells,
    });
    perf.measure('QF-Builder', 'No incremental changes', tBuild);
    return { data: null, currentRevitParameters };
  }

  // ── INITIAL BUILD PATH — process families in async chunks ──
  const newData: QuickFillItem[] = [];
  const existingByName = new Map(existingData.map(item => [item.Name, item]));
  const existingRowIndexByName = new Map(existingData.map((item, index) => [item.Name, index]));
  const existingByTypeId = new Map<string, QuickFillItem>();
  existingData.forEach((item) => {
    const typeKey = stableTypeKey(getItemFamilyTypeId(item));
    if (typeKey && !existingByTypeId.has(typeKey)) {
      existingByTypeId.set(typeKey, item);
    }
  });
  const getExistingRowIndex = (item: QuickFillItem, name: string): number =>
    existingRowIndexByName.get(name) ?? existingData.indexOf(item);
  let processed = 0;
  const totalFamilies = pipeFamilies.length;

  for (let fi = 0; fi < totalFamilies; fi++) {
    const family = pipeFamilies[fi];
    const renderableTypes = getRenderableTypesForFamily(family, projectVisibleTypeIndex, hasProjectElementRows);

    if (renderableTypes.length === 0) {
      if (hasProjectElementRows) {
        processed++;
        continue;
      }

      const name = family.name;
      const existingItem = existingByName.get(name);
      if (existingItem) {
        const existingRowIndex = getExistingRowIndex(existingItem, name);
        checkedProps.forEach((p) => {
          const resolved = p.revitParameter
            ? resolveParamValue(
                family.name,
                null,
                new Map(),
                p.revitParameter,
                instanceParamIndex,
                availableParameterNames,
                p.bindingScopeHint,
                instanceTargetIndex
              )
            : EMPTY_RESOLVED;
          applySourceResolvedValueToItem(
            existingItem,
            p.label,
            resolved,
            isEditedQuickFillCell(editedCells, existingRowIndex, p.label)
          );
        });
        (existingItem as any).__rowMode = 'familyType';
        (existingItem as any).__familyTypeKey = buildFamilyTypeKey(family.name, null);
        (existingItem as any).__familyTypeId = '';
        (existingItem as any).__category = family.category || '';
        applyModelElementIdsToItem(existingItem, family.name, null, null, modelElementIdIndex);
        newData.push(existingItem);
      } else {
        const item: QuickFillItem = { Name: name };
        checkedProps.forEach((p) => {
          const resolved = p.revitParameter
            ? resolveParamValue(
                family.name,
                null,
                new Map(),
                p.revitParameter,
                instanceParamIndex,
                availableParameterNames,
                p.bindingScopeHint,
                instanceTargetIndex
              )
            : EMPTY_RESOLVED;
          applyResolvedValueToItem(item, p.label, resolved, true);
        });
        (item as any).__rowMode = 'familyType';
        (item as any).__familyTypeKey = buildFamilyTypeKey(family.name, null);
        (item as any).__familyTypeId = '';
        (item as any).__category = family.category || '';
        applyModelElementIdsToItem(item, family.name, null, null, modelElementIdIndex);
        newData.push(item);
      }
    } else {
      for (const renderableType of renderableTypes) {
        const rowFamilyName = getRenderableFamilyName(family.name, renderableType);
        const name = buildFamilyTypeKey(rowFamilyName, renderableType.name);
        const renderableTypeKey = stableTypeKey(getRenderableTypeId(renderableType));
        const existingItem = existingByName.get(name) ?? (renderableTypeKey ? existingByTypeId.get(renderableTypeKey) : undefined);
        const typeMap = getTypeParamMap(renderableType.familyType);

        if (existingItem) {
          const existingRowIndex = getExistingRowIndex(existingItem, name);
          existingItem.Name = name;
          checkedProps.forEach(prop => {
            if (!prop.revitParameter) return;
            applySourceResolvedValueToItem(existingItem, prop.label,
              resolveParamValue(
                rowFamilyName,
                renderableType.name,
                typeMap,
                prop.revitParameter,
                instanceParamIndex,
                availableParameterNames,
                prop.bindingScopeHint,
                instanceTargetIndex,
                getRenderableTypeId(renderableType)
              ), isEditedQuickFillCell(editedCells, existingRowIndex, prop.label));
          });
          (existingItem as any).__rowMode = 'familyType';
          (existingItem as any).__familyTypeKey = name;
          (existingItem as any).__familyTypeId = getRenderableTypeId(renderableType) ?? '';
          (existingItem as any).__category = family.category || '';
          applyModelElementIdsToItem(
            existingItem,
            rowFamilyName,
            renderableType.name,
            getRenderableTypeId(renderableType),
            modelElementIdIndex
          );
          newData.push(existingItem);
        } else {
          const item: QuickFillItem = { Name: name };
          checkedProps.forEach(prop => {
            const resolved = prop.revitParameter
              ? resolveParamValue(
                  rowFamilyName,
                  renderableType.name,
                  typeMap,
                  prop.revitParameter,
                  instanceParamIndex,
                  availableParameterNames,
                  prop.bindingScopeHint,
                  instanceTargetIndex,
                  getRenderableTypeId(renderableType)
                )
              : EMPTY_RESOLVED;
            applyResolvedValueToItem(item, prop.label, resolved, true);
          });
          (item as any).__rowMode = 'familyType';
          (item as any).__familyTypeKey = name;
          (item as any).__familyTypeId = getRenderableTypeId(renderableType) ?? '';
          (item as any).__category = family.category || '';
          applyModelElementIdsToItem(
            item,
            rowFamilyName,
            renderableType.name,
            getRenderableTypeId(renderableType),
            modelElementIdIndex
          );
          newData.push(item);
        }
      }
    }

    processed++;

    // Yield every FAMILIES_PER_CHUNK families
    if (processed % FAMILIES_PER_CHUNK === 0) {
      onProgress(processed, totalFamilies);
      await yieldToMain();
      if (!isStillValid()) return { data: null, currentRevitParameters };
    }
  }

  emitPopulationDiagnostics('Initial build', newData, checkedProps, {
    familiesProcessed: totalFamilies,
    dataAlreadyInitialized,
    rowMode,
    projectVisibleTypes: projectVisibleTypeCount,
  });
  perf.measure('QF-Builder', 'Initial build done', tBuild, { rows: newData.length, cols: checkedProps.length });
  return { data: newData, currentRevitParameters };
}

export const __quickFillDataBuilderTestInternals = {
  buildDataAsync: buildQuickFillDataAsync,
  getProjectElementIndexes,
};
export {
  buildInstanceParamIndex,
  buildInstanceTargetIndex,
  buildModelElementIdIndex,
  buildProjectVisibleTypeIndex,
  buildProjectElementIndexes,
  getProjectElementIndexes,
} from './quickFillProjectIndexes';

export {
  cloneQuickFillDataRows,
  buildCurrentRevitParameters,
  buildDatasetCacheKey,
  buildBuiltDatasetCacheKey,
  buildCheckedPropertiesSignature,
  cloneBuiltDatasetCacheEntry,
  rememberBuiltDatasetCacheEntryWithPerf,
  quickFillBuiltDatasetCache,
};
export {
  buildAvailableParameterScopeIndex,
  resolveParamValue,
  resolveInstanceScopedParamValue,
  setItemValue,
  applyResolvedMetadataToItem,
  applyResolvedValueToItem,
  applySourceResolvedValueToItem,
  isEditedQuickFillCell,
  buildPopulationSummary,
  buildSampleRows,
  emitPopulationDiagnostics,
} from './quickFillParameterResolution';

export {
  createInstanceQuickFillItem,
  buildFamilyTypeKey,
  getRenderableFamilyName,
  buildRenderableFamilyTypeKey,
  buildInstanceRowLabel,
  resolveModelElementIdsForFamilyType,
  applyModelElementIdsToItem,
  getRenderableTypesForFamily,
  getRenderableTypeId,
  getItemFamilyTypeId,
} from './quickFillItemBuilders';

export type {
  PipeFamily,
  PipeElement,
  ProjectVisibleType,
  ProjectVisibleTypeIndex,
  ProjectElementIndexes,
  RenderableFamilyType,
  AvailableParameterMeta,
  ParameterBindingScope,
  AvailableParameterScopeEntry,
  ResolvedParam,
  CheckedProperty,
  CheckedPropertyWithLookup,
  PropertyPopulationSummary,
  QuickFillBuiltDatasetCacheEntry,
  QuickFillDatasetCacheKey,
} from './quickFillDataTypes';
